import { useQuery } from '@tanstack/react-query';

import type { PreorderDetailsResponse } from '@ekhodealer/ekho-common';
import type { UseQueryResult } from '@tanstack/react-query';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { preordersEndpoints } from '@config/server/apiEndpoints';

import { preorders } from './queryKeys';

/**
 * API function to get a single preorder's details by ID
 * @param servicesProviderId - Required services provider ID
 * @param preorderId - The preorder ID
 */
export const getPreorderDetails = async (
	servicesProviderId: string,
	preorderId: string,
): Promise<PreorderDetailsResponse> => {
	const response = await apiClient.get<PreorderDetailsResponse>(
		preordersEndpoints.details(servicesProviderId, preorderId),
	);
	return response.data;
};

/**
 * Hook for fetching a single preorder's details by ID
 * @param preorderId - The preorder ID
 */
export const useGetPreorderDetails = (preorderId: string | undefined): UseQueryResult<PreorderDetailsResponse> => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: preorders.detail(servicesProviderId, preorderId ?? '').queryKey,
		queryFn: () => {
			if (!preorderId) {
				throw new Error('Unable to fetch preorder without a preorder ID.');
			}
			return getPreorderDetails(servicesProviderId, preorderId);
		},
		enabled: Boolean(preorderId) && Boolean(servicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};
